import React from 'react';

type RegionDecision = {
  region: string;
  framework: string;
  status: number;
  latencyMs: number;
  fallback: boolean;
  source: 'network' | 'cache' | 'fallback' | 'static';
};

type Props = {
  baseUrl: string;
  endpointPath?: string;
  onDecision?: (decision: RegionDecision) => void;
  onError?: (message: string | null) => void;
  onLoadingChange?: (loading: boolean) => void;
};

const defaultEndpoint = '/api/v1/region';

const normalizeEndpoint = (baseUrl: string, path: string) => {
  if (!baseUrl) return path;
  const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const trimmedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
};

export function RegionDecisionCard({
  baseUrl,
  endpointPath = defaultEndpoint,
  onDecision,
  onError,
  onLoadingChange,
}: Props) {
  const [decision, setDecision] = React.useState<RegionDecision | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [fetchedAt, setFetchedAt] = React.useState<string | null>(null);
  const [decisionSource, setDecisionSource] = React.useState<RegionDecision['source'] | 'unknown'>(
    'unknown',
  );

  const endpoint = React.useMemo(
    () => normalizeEndpoint(baseUrl, endpointPath),
    [baseUrl, endpointPath],
  );

  const readDebugDecision = React.useCallback(() => {
    try {
      const debugFn = (
        window as unknown as { DWConsent?: { debug?: { regionDecision?: () => unknown } } }
      ).DWConsent?.debug?.regionDecision;
      if (typeof debugFn === 'function') {
        const snapshot = debugFn();
        if (snapshot && typeof snapshot === 'object') {
          const src = (snapshot as { source?: string }).source;
          return typeof src === 'string' ? (src as RegionDecision['source']) : undefined;
        }
      }
    } catch {
      /* ignore debug errors */
    }
    return undefined;
  }, []);

  const fetchDecision = React.useCallback(async () => {
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    setFetchedAt(null);
    const started = performance.now();
    try {
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      const latencyMs = performance.now() - started;
      const parseBody = async () => {
        try {
          return await response.json();
        } catch {
          return null;
        }
      };

      if (response.ok || response.status === 404) {
        const body = await parseBody();
        const region = typeof body?.region === 'string' ? body.region : 'eu-eea';
        const framework = typeof body?.framework === 'string' ? body.framework : undefined;
        const nextDecision: RegionDecision = {
          region,
          framework:
            framework ??
            (region.startsWith('us-')
              ? 'gpp'
              : region === 'uk' || region === 'eu-eea'
                ? 'tcf'
                : 'basic'),
          status: response.status,
          latencyMs,
          fallback: response.status === 404,
          source: response.status === 404 ? 'fallback' : 'network',
        };
        setDecision(nextDecision);
        setFetchedAt(new Date().toISOString());
        setError(null);
        const debugSource = readDebugDecision();
        setDecisionSource(debugSource ?? nextDecision.source);
        onDecision?.(nextDecision);
        onError?.(null);
        return;
      }

      setError(`${response.status} ${response.statusText || 'Unexpected response'}`);
      onError?.(`${response.status} ${response.statusText || 'Unexpected response'}`);
      setDecisionSource('unknown');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Region lookup failed');
      onError?.(err instanceof Error ? err.message : 'Region lookup failed');
      setDecisionSource('unknown');
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [endpoint, onDecision, onError, onLoadingChange, readDebugDecision]);

  React.useEffect(() => {
    const snapshot = readDebugDecision();
    if (snapshot) setDecisionSource(snapshot);
  }, [readDebugDecision]);

  React.useEffect(() => {
    void fetchDecision();
  }, [fetchDecision]);

  const clientRegion = (window as unknown as { __cmpRegion?: string }).__cmpRegion ?? null;
  const clientFramework = (window as unknown as { __cmpFramework?: string }).__cmpFramework ?? null;

  return (
    <div id="region-decision-card" className="rounded-lg border p-4 text-sm">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold">Region decision</h2>
        <button
          className="px-2 py-1 border rounded"
          onClick={() => void fetchDecision()}
          disabled={loading}
        >
          {loading ? 'Checking…' : 'Retry'}
        </button>
      </div>
      {error ? (
        <div className="text-sm text-red-600">
          {error}
          <div className="text-xs text-amber-600">
            We applied a GDPR-safe fallback while the Region API recovers.
          </div>
        </div>
      ) : decision ? (
        <dl className="space-y-1">
          <div className="flex gap-2">
            <dt className="text-gray-600">Region:</dt>
            <dd>
              <code>{decision.region}</code>
              {decision.fallback && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                  fallback
                </span>
              )}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-gray-600">Framework:</dt>
            <dd>
              <code>{decision.framework}</code>
            </dd>
          </div>
          <div className="flex gap-2 text-gray-600">
            <dt>Status:</dt>
            <dd>{decision.status}</dd>
          </div>
          <div className="flex gap-2 text-gray-600">
            <dt>Source:</dt>
            <dd>
              {decisionSource === 'unknown' ? 'unknown' : decisionSource}
              <span className="mx-2 text-gray-400">•</span>
              Latency: {Number.isFinite(decision.latencyMs)
                ? decision.latencyMs.toFixed(0)
                : '—'}{' '}
              ms
            </dd>
          </div>
          {fetchedAt && (
            <div className="flex gap-2 text-gray-600">
              <dt>Fetched:</dt>
              <dd>{new Date(fetchedAt).toLocaleTimeString()}</dd>
            </div>
          )}
          <div className="pt-2 border-t mt-2 text-xs text-gray-600 space-y-1">
            <div>
              Client region:
              <code className="ml-1">{clientRegion ?? 'unknown'}</code>
            </div>
            <div>
              Client framework:
              <code className="ml-1">{clientFramework ?? 'not-set'}</code>
            </div>
          </div>
        </dl>
      ) : (
        <div className="text-sm text-gray-500">Checking region endpoint…</div>
      )}
    </div>
  );
}
